/**
 * localStorage fallback for the watermark library (VE-24693) — **DISABLED**.
 *
 * `WATERMARK_LOCAL_FALLBACK_ENABLED` below is false, so no caller reaches the
 * functions in this module. It is kept, not deleted, so the crutch is one
 * constant away during rollout; expect it to be stripped once the registry is
 * live everywhere. The rest of this header describes what it did while on.
 *
 * The library is normally backed by aiWARE SDOs (see services/index.ts). The
 * registry id is no longer something an environment has to configure — there is
 * a `DEFAULT_WATERMARK_REGISTRY_ID` and runtime config only overrides it — so
 * what sends the saga here is not a missing id but a registry with no PUBLISHED
 * SCHEMA on this cluster: `resolveWatermarkRegistry` reports `unprovisioned`
 * and the saga serves the library from this browser instead, so the whole
 * feature stays usable while a cluster is being provisioned.
 *
 * Only `unprovisioned` comes here. A lookup that ERRORS reports `unreachable`
 * and fails the operation, because a transient 502 is not evidence that shared
 * storage does not exist, and silently diverting a save to one browser on that
 * basis is how a library quietly splits in two.
 *
 * NOTE: browser-local + per-user + per-device; not shared org-wide, and records
 * written here carry client-generated ids that exist in no registry, so they do
 * not migrate when one is provisioned. Since nothing enforces that this stays a
 * bridge, the UI says so: `WatermarkUnavailableNotice` renders off
 * `registryUnavailable` on the settings page and the create drawer. Writes here THROW on failure
 * rather than being swallowed, because reporting success on a save that did not
 * happen is how an operator loses work without being told.
 */
import { WatermarkDefinition, WatermarkDefinitionData } from './models';

/**
 * ⚠️ DISABLED. Nothing in the saga reaches this module while this is false.
 *
 * The browser-local store was a BUILD CRUTCH: it let the whole feature —
 * create, edit, delete, the editor picker, batch apply — be built and demoed
 * before the SDO registry existed on any cluster. That job is done, and keeping
 * it on is actively harmful: it presents per-device, per-user data through a
 * settings page that says "your organization's watermarks", and the way you
 * find out is two colleagues comparing lists. Anything written here is also
 * orphaned the moment the registry is provisioned, because the records carry
 * client-generated ids that exist in no registry.
 *
 * Left in place rather than deleted so the crutch is one constant away if an
 * environment turns out to need it again during rollout. **Expect to strip this
 * module and its callers once the registry is live everywhere** — it is dead
 * weight, not a supported mode. The notice that used to describe this store has
 * already been renamed for the state it reports rather than for this mechanism.
 */
export const WATERMARK_LOCAL_FALLBACK_ENABLED = false;

const LS_KEY = 'vtn-redact-watermark-library';

export function lsListWatermarks(): WatermarkDefinition[] {
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? (parsed as WatermarkDefinition[]) : [];
  } catch {
    return [];
  }
}

/**
 * Persist the list, or THROW.
 *
 * This used to swallow quota errors on the grounds that the fallback is only a
 * dev bridge. It is not: until a cluster has a published schema this is the
 * path every user on it takes, and one 2 MB image watermark is ~2.7M
 * characters — over half a typical 5 MB localStorage budget, so the second one
 * throws. Swallowing it meant the saga dispatched SUCCESS on a save that never
 * happened: the drawer closed, no error appeared, and the row was simply absent
 * after the refetch, with nothing to tell the operator why.
 */
function lsWrite(list: WatermarkDefinition[]): void {
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(list));
  } catch (err) {
    throw new Error(
      `Could not save the watermark library to this browser: ${
        err instanceof Error ? err.message : String(err)
      }`
    );
  }
}

/** Insert (no id) or update (id present) a record; returns the saved record. */
export function lsUpsertWatermark(
  data: WatermarkDefinitionData,
  id: string | undefined,
  newId: string,
  nowIso: string
): WatermarkDefinition {
  const list = lsListWatermarks();
  if (id) {
    const existing = list.find((w) => w.id === id);
    const record: WatermarkDefinition = {
      id,
      data,
      createdDateTime: existing?.createdDateTime ?? nowIso,
      modifiedDateTime: nowIso,
    };
    lsWrite(list.map((w) => (w.id === id ? record : w)));
    return record;
  }
  const record: WatermarkDefinition = {
    id: newId,
    data,
    createdDateTime: nowIso,
    modifiedDateTime: nowIso,
  };
  lsWrite([...list, record]);
  return record;
}

export function lsDeleteWatermark(id: string): void {
  lsWrite(lsListWatermarks().filter((w) => w.id !== id));
}
